import React, { useState, useEffect } from 'react';
import { Modal, Input, Table, Button, message } from 'antd';
import { SearchOutlined, UserOutlined, CloseOutlined, ClearOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL, DeleteUserText } from '../../../constants/Constants';

const DeleteUserForm = ({ isVisible, onClose, emailId }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isVisible || !emailId) return;
      
      setLoading(true);
      try {
        const response = await axios.get(API_BASE_URL + DeleteUserText.GET_COMPANY_USERS + emailId,{headers:{
          Authorization:`Bearer ${sessionStorage.getItem("authToken")}`
        }});
        setUserData(response.data || []);
      } catch (error) {
        console.error("Error fetching users:", error);
        message.error(DeleteUserText.FAILED_TO_LOAD);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isVisible, emailId]);

  const onSelectChange = (selectedKeys) => {
    setSelectedRowKeys(selectedKeys);
  };

  const showDeleteConfirm = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(DeleteUserText.SELECT_USERS_DELETE);
      return;
    }
    setDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const deleteUsersId = selectedRowKeys.map(key => {
        const user = userData.find(u => u.id === key);
        return user.userId;
      });

      const payload = {
        adminEmailId: emailId,
        deleteUsersId: deleteUsersId
      };
      
      const response = await axios.delete(API_BASE_URL+DeleteUserText.DELETE_COMPANY_USERS, {
        data: payload,
        headers:{
        Authorization:`Bearer ${sessionStorage.getItem("authToken")}`
        }
      });
      
      const newData = userData.filter(item => !selectedRowKeys.includes(item.id));
      setUserData(newData);
      setSelectedRowKeys([]);
      message.success(response.data || `${selectedRowKeys.length}${DeleteUserText.DELETE_SUCESS}`);
    } catch (error) {
      console.error("Error deleting users:", error);
      message.error(DeleteUserText.DELETE_FAILURE);
    } finally {
      setLoading(false);
      setDeleteModalVisible(false);
    }
  };

  const handleClearSelected = () => {
    setSelectedRowKeys([]);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchText('');
  };

  const handleClose = () => {
    setSelectedRowKeys([]);
    setSearchText('');
    onClose();
  };

  const filteredData = userData.filter(item => {
    const searchVal = searchText.toLowerCase();
    return (
      (item.userId && item.userId.toLowerCase().includes(searchVal)) ||
      (item.emailId && item.emailId.toLowerCase().includes(searchVal)) ||
      (item.role && item.role.toLowerCase().includes(searchVal))
    );
  });

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    hideSelectAll: true,
    renderCell: (checked, record, index, originNode) => {
      if (record.emailId === emailId) {
        return null;
      }
      return originNode;
    },
    columnWidth: 60,
  };

  const columns = [
    {
      title: DeleteUserText.USER_ID,
      dataIndex: 'userId',
      key: 'userId',
      align: 'center',
    },
    {
      title: DeleteUserText.EMAIL,
      dataIndex: 'emailId',
      key: 'emailId',
      align: 'center',
    },
    {
      title: DeleteUserText.ROLE,
      dataIndex: 'role',
      key: 'role',
      align: 'center',
    }
  ];

  return (
    <Modal
      title={
        <div className="pb-2 border-b border-gray-200 bg-white sticky top-0 z-10">
          <div className="text-[#1F405A] text-[18px] md:text-[20px] font-bold text-center">
            {DeleteUserText.TITLE}
          </div>
        </div>
      }
      open={isVisible}
      onCancel={handleClose}
      width={800}
      footer={null}
      closeIcon={<CloseOutlined />}
      maskClosable={false} 
      style={{ top: '25px' }}
      className="max-w-full"
    >   
      <div className="pt-2 flex flex-col sm:flex-row justify-between gap-2 mb-4">
        <div className="relative flex-1">
          <Input
            placeholder={DeleteUserText.SEARCH_PLACE_HOLDER}
            prefix={<SearchOutlined style={{ color: '#d9d9d9' }} />}
            value={searchText}
            onChange={handleSearch}
            className="w-full"
            suffix={
              searchText ? 
                <ClearOutlined 
                  onClick={handleClearSearch} 
                  style={{ color: '#999', cursor: 'pointer' }} 
                /> : null
            }
          />
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={handleClearSelected}
            disabled={selectedRowKeys.length === 0}
            className="whitespace-nowrap"
          >
            {DeleteUserText.CLEAR_SELECTED} ({selectedRowKeys.length})
          </Button>
          
          <Button
            type="primary"
            danger
            disabled={selectedRowKeys.length === 0}
            onClick={showDeleteConfirm}
            className="whitespace-nowrap"
          >
            {DeleteUserText.DELETE}
          </Button>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={{ pageSize: 5 }}
          loading={loading}
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onClick: () => {
              if (record.emailId === emailId) {
                message.warning(DeleteUserText.CANNOT_SELF_SELECT);
                return;
              }
              const selectedKey = record.id;
              const newSelectedRowKeys = [...selectedRowKeys];
              
              if (newSelectedRowKeys.includes(selectedKey)) {
                const index = newSelectedRowKeys.indexOf(selectedKey);
                newSelectedRowKeys.splice(index, 1);
              } else {
                newSelectedRowKeys.push(selectedKey);
              }
              
              setSelectedRowKeys(newSelectedRowKeys);
            },
            style: {
              cursor: record.emailId === emailId ? 'default' : 'pointer',
              backgroundColor: selectedRowKeys.includes(record.id) ? '#f0f5ff' : undefined,
            }
          })}
        />
      </div>

      <Modal
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText={DeleteUserText.YES}
        cancelText={DeleteUserText.NO}
        okButtonProps={{ danger: true }}
      >
        {DeleteUserText.CONFIRM_DELETE} {selectedRowKeys.length} user(s)?
      </Modal>
    </Modal>
  );
};

export default DeleteUserForm;